/**
 * Hooks TanStack Query pour la liste "Vos dossiers" de la mémoire-dossier (#271).
 *
 * Consomme GET /api/cases/:caseId/memory (CaseMemoryResponse) et
 * PATCH /api/cases/:caseId/memory (body: { digest: string }).
 *
 * Fan-out pattern : charge la liste de dossiers (useAllCases) puis lance
 * une query par dossier en parallèle (useQueries) avec un cap de 20 entrées.
 */

import { useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { ErrorService } from '@/lib/errors';
import { useCabinetId } from '@/hooks/useCabinetMemberMe';
import { useAllCases } from '@/hooks/useCases';
import { CaseMemoryResponseSchema } from '@hector/shared-types';
import type { CaseDigest, CaseMemoryResponse } from '@hector/shared-types';

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * Cache prefix shared between per-case queries and the invalidation sweep.
 *
 * Exported because case digests have a second writer — the agent's memory tools —
 * whose refresh is wired in `sessionDoneBridge.ts`.
 */
export const CASE_MEMORY_KEY = 'case-memory';

/**
 * Maximum number of cases we fan out to when fetching digests.
 *
 * WHY: each case maps to one GET request; without a cap this scales O(n) with
 * cabinet size, which causes unnecessary load on both the client and the API.
 * 20 is enough to fill the "Vos dossiers" card list with meaningful history.
 *
 * TODO(perso): replace with a dedicated backend enumeration endpoint
 * (GET /api/cases/memory-digests?limit=20) that returns only cases with a
 * non-null digest in a single DB query — eliminates the fan-out entirely.
 */
const CASE_MEMORY_LIMIT = 20;

/** staleTime for per-case memory queries — 2 min keeps the list fresh enough
 * without hammering the API every navigation event. */
const CASE_MEMORY_STALE_MS = 2 * 60 * 1_000;

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * A single entry in the "Vos dossiers" memory list.
 * Only cases whose digest text is non-empty appear here.
 */
export interface CaseMemoryEntry {
  /** UUID of the case. */
  caseId: string;
  /** Display name of the case (case.title). */
  caseName: string;
  /** Full digest text (use memoryExcerpt() for card previews). */
  digest: string;
  /**
   * ISO 8601 timestamp of the last digest mutation, or null if somehow absent.
   * Used for descending sort (most recently updated first).
   */
  updatedAt: string | null;
}

// ─── useCaseMemories ─────────────────────────────────────────────────────────

/**
 * Returns the list of cases that have a non-empty memory digest, sorted by
 * most-recently-updated first.
 *
 * Strategy:
 *   1. Load the full case list via useAllCases (cached, stale after 30s).
 *   2. Take the 20 most-recently-created cases as the fan-out population.
 *   3. Fetch GET /api/cases/:caseId/memory in parallel via useQueries.
 *   4. Filter to cases whose digest.digest is a non-empty trimmed string.
 *   5. Sort by updatedAt DESC (nulls last).
 *
 * `isLoading` is true until both the case list AND every memory query have
 * returned at least once (first-load UX — subsequent navigations use cache).
 */
export function useCaseMemories(): { data: CaseMemoryEntry[]; isLoading: boolean } {
  const cabinetId = useCabinetId();
  const { data: allCases, isLoading: casesLoading } = useAllCases();

  // Sort by created_at DESC before slicing so we consistently target the 20
  // most-recently-created cases regardless of the order useAllCases returns.
  // useAllCases calls fetchAllCases which does NOT guarantee sort order.
  const casesToQuery = [...(allCases ?? [])]
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .slice(0, CASE_MEMORY_LIMIT);

  // Enabled only when cabinetId is resolved AND the case list has settled —
  // avoids issuing memory requests against a partial case list during boot.
  const casesLoaded = !casesLoading && allCases !== undefined;

  const memoryQueries = useQueries({
    queries: casesToQuery.map((c) => ({
      queryKey: [CASE_MEMORY_KEY, c.id] as const,
      // Explicit return type mirrors the pattern in useJobPolling.ts —
      // without it TanStack Query v5 may widen the inference to `unknown`,
      // making property access on q.data a type error downstream.
      queryFn: async (): Promise<CaseMemoryResponse> => {
        // Parse at the network boundary so shape mismatches surface immediately
        // with a Zod error rather than silently producing undefined fields.
        const raw = await apiClient.get<unknown>(`/cases/${c.id}/memory`);
        return CaseMemoryResponseSchema.parse(raw);
      },
      enabled: !!cabinetId && casesLoaded,
      staleTime: CASE_MEMORY_STALE_MS,
    })),
  });

  // Build the filtered, sorted entry list from settled queries.
  const entries: CaseMemoryEntry[] = [];
  for (let i = 0; i < casesToQuery.length; i++) {
    const q = memoryQueries[i];
    const c = casesToQuery[i];
    // Defensive: both must exist (array lengths are always equal but TypeScript
    // doesn't know that when iterating with index access).
    if (!q || !c) continue;

    const digestRecord = q.data?.digest;
    // Keep only cases with a non-empty persisted digest — otherwise the card
    // would render blank and confuse the lawyer.
    if (!digestRecord || !digestRecord.digest.trim()) continue;

    entries.push({
      caseId: c.id,
      caseName: c.title,
      digest: digestRecord.digest,
      // CaseDigestSchema.updated_at is z.string() (non-optional), so this is
      // always a string when digest is non-null. The | null typing accommodates
      // hypothetical future schema relaxation without breaking callers.
      updatedAt: digestRecord.updated_at ?? null,
    });
  }

  // Most-recently-updated first; cases with no updatedAt go last.
  entries.sort((a, b) => {
    if (!a.updatedAt && !b.updatedAt) return 0;
    if (!a.updatedAt) return 1;
    if (!b.updatedAt) return -1;
    // ISO 8601 strings sort lexicographically in the same order as dates.
    return b.updatedAt.localeCompare(a.updatedAt);
  });

  // isLoading covers both axes:
  //  - casesLoading: the case list hasn't arrived yet.
  //  - q.isLoading: status=pending AND fetchStatus=fetching (first fetch, no
  //    cached data). False for disabled queries (fetchStatus=idle) so we don't
  //    stay stuck in loading when cabinetId is not yet resolved.
  const isLoading = casesLoading || memoryQueries.some((q) => q.isLoading);

  return { data: entries, isLoading };
}

// ─── useUpdateCaseMemory ──────────────────────────────────────────────────────

/**
 * Mutation to rewrite a case's memory digest (PATCH /api/cases/:caseId/memory).
 *
 * On success invalidates:
 *   - The per-case key `['case-memory', caseId]` — refreshes the individual digest.
 *   - The general key `['case-memory']`        — refreshes useCaseMemories list.
 *
 * On error routes through ErrorService with a user-visible French message and
 * reports to PostHog for observability (same pattern as useUpdateMemoryBlock).
 */
export function useUpdateCaseMemory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ caseId, digest }: { caseId: string; digest: string }) =>
      apiClient.patch<{ digest: CaseDigest | null }>(`/cases/${caseId}/memory`, { digest }),

    onSuccess: (_data, variables) => {
      // Invalidate specific entry first so the detail view refreshes immediately,
      // then sweep the general key to reorder/filter the "Vos dossiers" list.
      queryClient.invalidateQueries({ queryKey: [CASE_MEMORY_KEY, variables.caseId] });
      queryClient.invalidateQueries({ queryKey: [CASE_MEMORY_KEY] });
    },

    onError: (error) => {
      ErrorService.handle(
        error instanceof Error
          ? error
          : new Error('Erreur lors de la mise à jour de la mémoire du dossier'),
        {
          userMessage:
            "La mise à jour de la mémoire du dossier n'a pas abouti. Veuillez réessayer.",
          context: { action: 'update_case_memory' },
        },
      );
    },
  });
}
